import { create } from "zustand";
import type { Bubble } from "../components/chat/ChatPanel/types";

export type ChatActions = {
  prefillAndFocus: (text: string) => void;
  addMessages: (msgs: Bubble[]) => void;
  sendMessage: (text: string, opts?: { synthetic?: boolean }) => Promise<void>;
  appendQuotedPlace: (name: string) => void;
  setSuggestedReplies: (replies: string[]) => void;
};

export type InFlightTurn = {
  tripId: string;
  scopeKey?: string;
  scopeLabel: string | null;
  userBubble: Bubble;
  assistantBubble: Bubble;
  syntheticBubble?: Bubble;
  abortController: AbortController;
  isFlood?: boolean;
};

type StartTurnArgs = {
  tripId: string;
  scopeKey?: string;
  scopeLabel: string | null;
  userBubble: Bubble;
  assistantBubble: Bubble;
  syntheticBubble?: Bubble;
  isFlood?: boolean;
};

export type FloodProgress = {
  status: "idle" | "running" | "done";
  total: number;
  done: number;
  lastUpdatedLabel: string | null;
};

export type FloodLine = { place: string; destination: string };

export type FloodBoxCollapse = "auto-open" | "user-collapsed" | "auto-collapsed";

const FLOOD_IDLE: FloodProgress = { status: "idle", total: 0, done: 0, lastUpdatedLabel: null };

type ChatStore = {
  actions: ChatActions | null;
  pendingMessage: { tripId: string; message: string } | null;
  inFlightTurn: InFlightTurn | null;
  floodTurns: Record<string, InFlightTurn>;
  floodProgress: FloodProgress;
  floodLines: FloodLine[];
  floodBoxCollapse: FloodBoxCollapse;
  register: (actions: ChatActions) => void;
  unregister: () => void;
  setPending: (tripId: string, message: string) => void;
  consumePending: (tripId: string) => string | null;
  startTurn: (args: StartTurnArgs) => void;
  updateAssistantContent: (delta: string) => void;
  updateSteps: (step: string) => void;
  addDroppedPlace: (drop: { name: string; reason: string }) => void;
  removeSynthetic: () => void;
  completeTurn: () => void;
  failTurn: () => void;
  abortTurn: () => void;
  clearInFlightTurn: () => void;
  startFloodTurn: (scopeKey: string, args: StartTurnArgs) => void;
  completeFloodTurn: (scopeKey: string) => void;
  failFloodTurn: (scopeKey: string) => void;
  abortAllFloodTurns: () => void;
  startFlood: (total: number) => void;
  updateFloodProgress: (done: number, total: number, label: string) => void;
  finishFlood: () => void;
  resetFlood: () => void;
  addFloodLine: (place: string, destination: string) => void;
  setFloodBoxCollapse: (state: FloodBoxCollapse) => void;
};

function withAssistantBubble(
  s: ChatStore,
  updater: (b: Bubble) => Partial<Bubble>,
): Partial<ChatStore> {
  if (!s.inFlightTurn) return s;
  return {
    inFlightTurn: {
      ...s.inFlightTurn,
      assistantBubble: { ...s.inFlightTurn.assistantBubble, ...updater(s.inFlightTurn.assistantBubble) },
    },
  };
}

export const useChatStore = create<ChatStore>((set, get) => ({
  actions: null,
  pendingMessage: null,
  inFlightTurn: null,
  floodTurns: {},
  floodProgress: { ...FLOOD_IDLE },
  floodLines: [],
  floodBoxCollapse: "auto-collapsed",
  register: (actions) => set({ actions }),
  unregister: () => set({ actions: null }),
  setPending: (tripId, message) => set({ pendingMessage: { tripId, message } }),
  consumePending: (tripId) => {
    const { pendingMessage } = get();
    if (!pendingMessage || pendingMessage.tripId !== tripId) return null;
    set({ pendingMessage: null });
    return pendingMessage.message;
  },
  startTurn: (args) =>
    set({ inFlightTurn: { ...args, abortController: new AbortController() } }),
  updateAssistantContent: (delta) =>
    set((s) => withAssistantBubble(s, (b) => ({ content: b.content + delta }))),
  updateSteps: (step) =>
    set((s) => withAssistantBubble(s, (b) => ({ steps: [...(b.steps ?? []), step] }))),
  addDroppedPlace: (drop) =>
    set((s) => withAssistantBubble(s, (b) => ({ droppedPlaces: [...(b.droppedPlaces ?? []), drop] }))),
  removeSynthetic: () =>
    set((s) => {
      if (!s.inFlightTurn) return s;
      const { syntheticBubble: _, ...rest } = s.inFlightTurn;
      return { inFlightTurn: rest };
    }),
  completeTurn: () => set({ inFlightTurn: null }),
  failTurn: () => set({ inFlightTurn: null }),
  abortTurn: () => {
    const { inFlightTurn } = get();
    if (inFlightTurn) inFlightTurn.abortController.abort();
    set({ inFlightTurn: null });
  },
  clearInFlightTurn: () => set({ inFlightTurn: null }),
  startFloodTurn: (scopeKey, args) =>
    set((s) => ({
      floodTurns: {
        ...s.floodTurns,
        [scopeKey]: { ...args, abortController: new AbortController() },
      },
    })),
  completeFloodTurn: (scopeKey) =>
    set((s) => {
      const { [scopeKey]: _, ...rest } = s.floodTurns;
      return { floodTurns: rest };
    }),
  failFloodTurn: (scopeKey) =>
    set((s) => {
      const { [scopeKey]: _, ...rest } = s.floodTurns;
      return { floodTurns: rest };
    }),
  abortAllFloodTurns: () => {
    const { floodTurns } = get();
    for (const turn of Object.values(floodTurns)) {
      turn.abortController.abort();
    }
    set({ floodTurns: {} });
  },
  startFlood: (total) =>
    set({
      floodProgress: { status: "running", total, done: 0, lastUpdatedLabel: null },
      floodLines: [],
      floodBoxCollapse: "auto-open",
    }),
  updateFloodProgress: (done, total, label) =>
    set((s) => ({ floodProgress: { ...s.floodProgress, done, total, lastUpdatedLabel: label } })),
  finishFlood: () =>
    set((s) => ({
      floodProgress: { ...s.floodProgress, status: "done" },
      floodBoxCollapse: s.floodBoxCollapse === "user-collapsed" ? "user-collapsed" : "auto-collapsed",
    })),
  resetFlood: () =>
    set({ floodProgress: { ...FLOOD_IDLE }, floodLines: [], floodBoxCollapse: "auto-collapsed" }),
  addFloodLine: (place, destination) =>
    set((s) => ({ floodLines: [...s.floodLines, { place, destination }] })),
  setFloodBoxCollapse: (state) =>
    set({ floodBoxCollapse: state }),
}));
